import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Award, ShieldCheck, Wallet, Clock, Layers, Eye, Home as HomeIcon, Building2, Factory, Landmark } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { heroImage, poleImages, projects } from "@/data/projects";
import Reveal from "@/components/motion/Reveal";
import SectionHeading from "@/components/motion/SectionHeading";
import Marquee from "@/components/motion/Marquee";
import CtaBand from "@/components/layout/CtaBand";

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24">
        <img
          src={heroImage}
          alt="Chantier de construction moderne OMNIBÂT"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom, rgba(11,13,15,0.72) 0%, rgba(11,13,15,0.45) 45%, rgba(11,13,15,0.97) 100%)" }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-blueprint opacity-60" aria-hidden="true" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-16 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          data-testid="hero-kicker"
          className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-brand [text-shadow:0_1px_14px_rgba(11,13,15,0.95)]"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tight text-5xl sm:text-7xl lg:text-[6.5rem]">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "text-brand" : "text-cream"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.14, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          data-testid="hero-subtitle"
          className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-fog"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row gap-3"
        >
          <Link
            to="/devis"
            data-testid="hero-request-quote-button"
            className="group flex items-center justify-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-hover"
          >
            {t.hero.ctaQuote}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/poles"
            data-testid="hero-view-portfolio-button"
            className="flex items-center justify-center gap-3 border border-cream/30 px-8 py-4 font-display text-lg font-bold uppercase tracking-[0.12em] text-cream backdrop-blur-sm transition-colors hover:border-brand hover:text-brand"
          >
            {t.hero.ctaServices}
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-14 grid grid-cols-3 gap-6 border-t border-cream/15 pt-7 max-w-2xl"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s) => (
            <div key={s.label}>
              <p className="font-display text-3xl sm:text-4xl font-bold text-brand">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-fog">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 hidden lg:flex items-center gap-3 text-fog"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="h-4 w-4 text-brand" />
        </motion.span>
      </motion.div>
    </section>
  );
};

const PromiseSection = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="promise-section" className="relative bg-ink py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{t.promise.chapter}</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display font-bold uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl text-cream">
                {t.promise.title}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:pt-10">
            <Reveal delay={0.15}>
              <p className="text-base sm:text-lg leading-relaxed text-fog">{t.promise.text}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-8 border-l-2 border-brand pl-5 font-display text-xl uppercase tracking-wide text-cream">
                « {t.promise.signature} »
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const PolesSection = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="poles-section" className="relative bg-surface py-20 lg:py-32">
      <div className="absolute inset-0 bg-blueprint opacity-50" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading chapter={t.polesSection.chapter} title={t.polesSection.title} subtitle={t.polesSection.subtitle} testid="poles-heading" />
          <Reveal delay={0.2} className="mb-12 lg:mb-16">
            <Link
              to="/poles"
              data-testid="poles-view-all-link"
              className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand hover:text-brand-hover"
            >
              {t.polesSection.viewAll}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {t.poles.map((pole, i) => (
            <Reveal key={pole.slug} delay={0.06 * i} className="h-full">
              <Link
                to={`/poles#${pole.slug}`}
                data-testid={`pole-card-${pole.slug}`}
                className="group relative flex h-full flex-col bg-panel transition-colors hover:bg-[#20262e]"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={poleImages[pole.slug]}
                    alt={pole.title}
                    loading="lazy"
                    className="h-full w-full object-cover grayscale-[45%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-panel to-transparent" aria-hidden="true" />
                  <span className="absolute left-5 top-4 font-mono text-xs tracking-[0.25em] text-brand">{pole.num}</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-xl font-bold uppercase tracking-wide leading-tight text-cream">{pole.title}</h3>
                  <p className="mt-2 text-sm italic text-fog">{pole.tagline}</p>
                  <span className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand">
                    {t.polesSection.discover}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
                <span className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-brand transition-transform duration-500 group-hover:scale-x-100" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const SolutionsSection = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="solutions-section" className="relative border-y border-line bg-ink py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading chapter={t.solutionsSection.chapter} title={t.solutionsSection.title} subtitle={t.solutionsSection.subtitle} testid="solutions-heading" />
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {t.solutionsSection.items.map((item, i) => (
            <Reveal key={item.num} delay={0.08 * i}>
              <div data-testid={`solution-card-${item.num}`} className="group border-t border-line pt-6 transition-colors hover:border-brand">
                <p className="font-display text-6xl font-black text-outline transition-colors group-hover:text-brand group-hover:[-webkit-text-stroke:0px]">
                  {item.num}
                </p>
                <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-wide text-cream">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-fog">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="process-section" className="relative bg-surface py-20 lg:py-32">
      <div className="absolute inset-0 bg-blueprint opacity-50" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading chapter={t.process.chapter} title={t.process.title} subtitle={t.process.subtitle} testid="process-heading" />
        <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {t.process.steps.map((step, i) => (
            <Reveal key={step.num} delay={0.06 * i} className="h-full">
              <div data-testid={`process-step-${step.num}`} className="group relative h-full bg-panel p-7 transition-colors hover:bg-[#20262e]">
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-4xl font-black text-brand">{step.num}</span>
                  <span className="h-px flex-1 bg-line transition-colors group-hover:bg-brand/50" aria-hidden="true" />
                </div>
                <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-wide text-cream">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fog">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProjectsPreview = () => {
  const { t } = useLanguage();
  const featured = [projects[0], projects[2], projects[4]];
  return (
    <section data-testid="projects-preview-section" className="bg-ink py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading chapter={t.projectsSection.chapter} title={t.projectsSection.title} subtitle={t.projectsSection.subtitle} testid="projects-preview-heading" />
          <Reveal delay={0.2} className="mb-12 lg:mb-16">
            <Link
              to="/realisations"
              data-testid="projects-view-all-button"
              className="group flex items-center gap-3 border border-line px-6 py-3 font-display text-base font-bold uppercase tracking-[0.12em] text-cream transition-colors hover:border-brand hover:text-brand"
            >
              {t.projectsSection.cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={0.08 * i}>
              <Link to="/realisations" data-testid={`project-preview-${p.id}`} className="group block">
                <div className="relative aspect-[4/5] overflow-hidden border border-line">
                  <img
                    src={p.image}
                    alt={p.title.fr}
                    loading="lazy"
                    className="h-full w-full object-cover transition-all duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" aria-hidden="true" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">{p.location}</p>
                    <h3 className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-cream">{p.title[t.lang]}</h3>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const whyIcons = [Award, ShieldCheck, Wallet, Clock, Layers, Eye];
const clientIcons = [HomeIcon, Building2, Factory, Landmark];

const WhySection = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="why-section" className="border-y border-line bg-surface py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading chapter={t.why.chapter} title={t.why.title} testid="why-heading" />
        <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {t.why.items.map((item, i) => {
            const Icon = whyIcons[i];
            return (
              <Reveal key={item.title} delay={0.06 * i} className="h-full">
                <div data-testid={`why-card-${i + 1}`} className="group h-full bg-panel p-7 transition-colors hover:bg-[#20262e]">
                  <Icon className="h-7 w-7 text-brand" strokeWidth={1.5} />
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-cream">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fog">{item.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ClientsSection = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="clients-section" className="bg-ink py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading chapter={t.clients.chapter} title={t.clients.title} testid="clients-heading" />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {t.clients.items.map((item, i) => {
            const Icon = clientIcons[i];
            return (
              <Reveal key={item.title} delay={0.08 * i}>
                <div data-testid={`client-card-${i + 1}`} className="group border border-line bg-surface p-7 transition-colors hover:border-brand">
                  <Icon className="h-8 w-8 text-brand" strokeWidth={1.25} />
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-cream">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fog">{item.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <PromiseSection />
    <PolesSection />
    <SolutionsSection />
    <ProcessSection />
    <ProjectsPreview />
    <WhySection />
    <ClientsSection />
    <CtaBand />
  </>
);

export default Home;
